import React, { useEffect, useMemo, useRef } from 'react';

import Table, { TableColumnAlignment, TableSortDirection } from '@components/table/Table';
import { Table as UswdsTable } from '@trussworks/react-uswds';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@src/routes/dashboard';

import useGetRegulatorHelpRequests from '@api/services/dashboard/hooks/useGetRegulatorHelpRequests';
import { useHelpCategoriesQuery } from '@api/services/lookup/hooks';
import { RegulatorDashboardSortColumn } from '@api/services/dashboard/types';
import { HelpInquiryStatus } from '@api/services/help/types';
import { useHelpRequestStore, useTableStore } from '@stores';

import HelpRequestActions from './HelpRequestActions';
import HelpRequestStatus from './HelpRequestStatus';
import HelpRequestFilters from './HelpRequestFilters';
import { HelpRequestStatuses } from '@utils/enums';
import { formatPhoneNumber, renderInquirySummaryCell, formatRequestType } from './HelpRequestTable.helpers';

import './helpRequestTable.scss';

type HelpRequestTableProps = {
  view?: 'unclaimed' | 'claimed' | 'all';
};

const tableIdByView: Record<NonNullable<HelpRequestTableProps['view']>, string> = {
  unclaimed: 'unclaimed-request-table',
  claimed: 'claimed-request-table',
  all: 'help-request-table',
};

const parseSearchList = (value?: string | null): string[] =>
  value
    ? value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean)
    : [];

const buildDashboardSearch = ({
  counties = [],
  districtCodes = [],
  helpCategories = [],
  pageNumber = 1,
  states = [],
  statuses = [],
  view = 'all',
}: {
  counties?: string[];
  districtCodes?: string[];
  helpCategories?: string[];
  pageNumber?: number;
  states?: string[];
  statuses?: string[];
  view?: 'unclaimed' | 'claimed' | 'all';
}) => ({
  district: districtCodes.length ? districtCodes.join(',') : undefined,
  status: statuses.length ? statuses.join(',') : undefined,
  states: states.length ? states.join(',') : undefined,
  counties: counties.length ? counties.join(',') : undefined,
  helpCategories: helpCategories.length ? helpCategories.join(',') : undefined,
  page: pageNumber > 1 ? pageNumber : undefined,
  tab: view !== 'unclaimed' ? view : undefined,
});

const HelpRequestTable = ({ view = 'all' }: HelpRequestTableProps) => {
  const navigate = useNavigate();
  const {
    getPageNumber,
    getPageSize,
    getSortColumn,
    getSortDirection,
    setPageNumber,
    setSortColumn,
    setSortDirection,
  } = useTableStore();

  const {
    districts,
    statuses,
    states,
    counties,
    helpCategories: categories,
    setDistricts,
    setStatuses,
    setHelpCategories,
    setStates,
    setCounties,
  } = useHelpRequestStore();

  const {
    district: districtsQueryParam,
    status: statusesQueryParam,
    states: statesQueryParam,
    counties: countiesQueryParam,
    helpCategories: helpCategoriesQueryParam,
    page: pageQueryParam,
  } = Route.useSearch();

  const hasAppliedUrlSearch = useRef(false);
  const helpRequestTableId = tableIdByView[view];

  const columnOrder = useMemo(
    () => [
      { name: 'actions', alignment: 'center' as TableColumnAlignment },
      ...(view !== 'unclaimed' ? [{ name: 'status' }] : []),
      { name: 'helpCategories' },
      { name: 'reason' },
      { name: 'projectName' },
      { name: 'district' },
      { name: 'state' },
      { name: 'county' },
      ...(view === 'all' ? [{ name: 'regulatorName' }] : []),
      { name: 'requestType' },
      { name: 'email' },
      { name: 'phone' },
      { name: 'createdDate' },
    ],
    [view]
  );

  const pageNumber = getPageNumber(helpRequestTableId);
  const pageSize = getPageSize(helpRequestTableId);
  const sortColumn = getSortColumn(helpRequestTableId);
  const sortDirection = getSortDirection(helpRequestTableId);

  const queryStatuses = useMemo(() => {
    if (view === 'unclaimed') {
      return `${HelpRequestStatuses.Open}`;
    }

    return statuses.length ? statuses.join(',') : undefined;
  }, [statuses, view]);

  const queryClaimed = view === 'claimed';

  const payload = useMemo(
    () => ({
      claimed: queryClaimed,
      column: sortColumn as RegulatorDashboardSortColumn,
      counties: counties.length ? counties.join(',') : undefined,
      dir: sortDirection,
      districts: districts.length ? districts.join(',') : undefined,
      helpCategories: categories.length ? categories.join(',') : undefined,
      page: pageNumber,
      size: pageSize,
      states: states.length ? states.join(',') : undefined,
      statuses: queryStatuses,
    }),
    [
      categories,
      counties,
      districts,
      pageNumber,
      pageSize,
      queryClaimed,
      queryStatuses,
      sortColumn,
      sortDirection,
      states,
    ]
  );

  const { data } = useGetRegulatorHelpRequests(payload);
  const { data: helpCategoryOptions = [] } = useHelpCategoriesQuery({ active: true });

  const items = useMemo(() => data?.items ?? [], [data]);

  const rowData = useMemo(
    () =>
      items.map((item) => ({
        actions: (
          <HelpRequestActions
            district={item.district}
            helpInquiryId={item.helpInquiryID}
            projectId={item.projectID}
            requestId={item.requestID}
            requestStatus={item.status}
            requestType={item.requestType}
            status={item.status as HelpInquiryStatus}
            version={item.version}
          />
        ),
        district: item.district,
        state: item.state?.trim() || 'Undetermined',
        county: item.county?.trim() || 'Undetermined',
        email: (
          <a className='email-value' href={`mailto:${item.email}`}>
            {item.email}
          </a>
        ),
        phone: (
          <span className='phone-value'>
            {formatPhoneNumber(item.phoneCountryCode, item.phoneNumber, item.phoneExtension)}   
          </span>
        ),
        helpCategories: item.helpCategories.join(', '),
        reason: renderInquirySummaryCell(item.reason),
        id: item.helpInquiryID,
        createdDate: item.createdDate,
        projectName: item.projectName,
        regulatorName: item.regulatorName,
        requestID: item.requestID,
        requestType: formatRequestType(item.requestType),
        status: <HelpRequestStatus value={item.status} />,
      })),
    [items]
  );

  const handlePageChange = (nextPageNumber: number) => {
    navigate({
      to: '/dashboard',
      search: () =>
        buildDashboardSearch({
          counties,
          districtCodes: districts,
          helpCategories: categories,
          pageNumber: nextPageNumber,
          states,
          statuses,
          view,
        }),
      replace: true,
    });
  };

  const handleSortDirectionChange = (nextSortDirection: TableSortDirection) => {
    setSortDirection(helpRequestTableId, nextSortDirection);
  };

  const handleSortNameChange = (nextSortColumn: string) => {
    setSortColumn(helpRequestTableId, nextSortColumn as RegulatorDashboardSortColumn);
  };

  const categoryTooltipContent = useMemo(
    () => (
      <UswdsTable compact bordered fullWidth className='margin-top-0 padding-0'>
        <thead>
          <tr>
            <th scope='col'>Category Type</th>
            <th scope='col'>Description</th>
          </tr>
        </thead>
        <tbody>
          {helpCategoryOptions.map((item) => (
            <tr key={item.category}>
              <td>{item.displayName}</td>
              <td>{item.description}</td>
            </tr>
          ))}
        </tbody>
      </UswdsTable>
    ),
    [helpCategoryOptions]
  );

  const headerData = useMemo(
    () => [
      [
        {
          label: 'Actions',
          name: 'actions',
          isSortable: false,
          width: '6%',
        },
        ...(view !== 'unclaimed'
          ? [
              {
                label: 'Inquiry Status',
                name: 'status',
                width: '7%',
              },
            ]
          : []),
        {
          label: 'Inquiry Categories',
          name: 'helpCategories',
          helpText: categoryTooltipContent,
          width: view === 'all' ? '10%' : '12%',
        },
        {
          label: 'Inquiry',
          name: 'reason',
          isSortable: false,
          width: view === 'all' ? '12%' : '15%',
        },
        {
          label: 'Project Name',
          name: 'projectName',
          width: view === 'all' ? '11%' : '12%',
        },
        {
          label: 'District',
          name: 'district',
          width: '7%',
        },
        {
          label: 'State',
          name: 'state',
          width: '3%',
        },
        {
          label: 'County',
          name: 'county',
          width: view === 'all' ? '7%' : '8%',
        },
        ...(view === 'all'
          ? [
              {
                label: 'Assisting Regulator',
                name: 'regulatorName',
                width: '6%',
              },
            ]
          : []),
        {
          label: 'Request Type',
          name: 'requestType',
          width: view === 'all' ? '9%' : '11%',
        },
        {
          label: 'Email',
          name: 'email',
          width: view === 'all' ? '14%' : '12%',
        },
        {
          label: 'Phone',
          name: 'phone',
          isSortable: false,
          width: '7%',
        },
        {
          defaultSortDirection: 'desc' as TableSortDirection,
          label: 'Date Submitted',
          name: 'createdDate',
          width: view === 'all' ? '1%' : '2%',
        },
      ],
    ],
    [categoryTooltipContent, view]
  );

  // set query parameters from URL query string into state
  useEffect(() => {
    if (hasAppliedUrlSearch.current) return;

    const districtList = parseSearchList(districtsQueryParam);
    const statusList = parseSearchList(statusesQueryParam);
    const statesList = parseSearchList(statesQueryParam);
    const countiesList = parseSearchList(countiesQueryParam);
    const categoryList = parseSearchList(helpCategoriesQueryParam);

    districtList.length && setDistricts(districtList);
    statusList.length && setStatuses(statusList);
    statesList.length && setStates(statesList);
    countiesList.length && setCounties(countiesList);
    categoryList.length && setHelpCategories(categoryList);

    hasAppliedUrlSearch.current = true;
  }, [
    helpRequestTableId,
    districtsQueryParam,
    statusesQueryParam,
    helpCategoriesQueryParam,
    setDistricts,
    setStatuses,
    setHelpCategories,
    statesQueryParam,
    countiesQueryParam,
    setStates,
    setCounties,
  ]);

  // Set default sort column and direction
  useEffect(() => {
    setSortColumn(helpRequestTableId, 'createdDate');
    setSortDirection(helpRequestTableId, 'desc');
  }, [helpRequestTableId, setSortColumn, setSortDirection]);

  // Set page number if in query string
  useEffect(() => {
    setPageNumber(helpRequestTableId, pageQueryParam);
  }, [helpRequestTableId, setPageNumber, pageQueryParam]);

  return (
    <div className='table-content help-request-table-content'>
      <HelpRequestFilters tableId={helpRequestTableId} view={view} />
      <Table
        className='dashboard-request-table'
        columnOrder={columnOrder}
        headerData={headerData}
        id={helpRequestTableId}
        onPageChange={handlePageChange}
        onSortDirectionChange={handleSortDirectionChange}
        onSortNameChange={handleSortNameChange}
        rowData={rowData ?? []}
        isScrollable
        sortColumn={sortColumn ?? 'createdDate'}
        sortDirection={sortDirection ?? 'desc'}
        totalRecords={data?.totalCount ?? 0}
      />
    </div>
  );
};

export default HelpRequestTable;
